import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBoxOpen,
  faShoppingBag,
  faCalendarAlt,
  faMapMarkerAlt,
  faReceipt,
  faArrowLeft,
  faCheckCircle,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import PatientPortalLayout from "../../components/Patient/PatientPortalLayout";
import styles from "../../components/Patient/PatientPortal.module.css";

const PatientOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [patientName, setPatientName] = useState("Patient");
  const navigate = useNavigate();

  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("userId");

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const storedName = localStorage.getItem("userName");
        if (storedName && storedName !== "undefined" && storedName !== "null") {
          setPatientName(storedName);
        }

        if (userId && token) {
          const res = await axios.get(`/api/shop/orders/patient/${userId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setOrders(res.data.orders || []);
        }
      } catch (err) {
        console.error("Failed to load patient orders", err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [userId, token]);

  return (
    <PatientPortalLayout activePage="orders" patientName={patientName}>
      <div className={styles.mainContainer} style={{ maxWidth: "1000px", margin: "0 auto", padding: "2rem 1.5rem" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem", marginBottom: "2rem", borderBottom: "1px solid #e2e8f0", paddingBottom: "1.25rem" }}>
          <div>
            <button
              onClick={() => navigate("/patient-dashboard")}
              style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "#f1f5f9", border: "none", color: "#475569", padding: "0.45rem 1rem", borderRadius: "9999px", fontSize: "0.85rem", fontWeight: 700, cursor: "pointer", marginBottom: "0.75rem", transition: "all 0.2s" }}
            >
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Dashboard
            </button>
            <h1 style={{ fontSize: "1.8rem", fontWeight: 900, color: "#0f172a", margin: 0, display: "flex", alignItems: "center", gap: "10px" }}>
              <FontAwesomeIcon icon={faShoppingBag} style={{ color: "#2d6a4f" }} />
              My Current Orders
            </h1>
            <p style={{ color: "#64748b", margin: "4px 0 0", fontSize: "0.95rem" }}>
              Track medical wellness supplies and products ordered from ReformMe Shop
            </p>
          </div>

          <button
            onClick={() => navigate("/shop")}
            style={{ padding: "0.75rem 1.5rem", background: "linear-gradient(135deg, #2d6a4f, #1e4d38)", color: "#ffffff", border: "none", borderRadius: "9999px", fontWeight: 800, fontSize: "0.9rem", cursor: "pointer", boxShadow: "0 4px 12px rgba(45,106,79,0.25)" }}
          >
            🛒 Browse Wellness Shop
          </button>
        </div>

        {/* Orders List */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "4rem 1rem", color: "#64748b" }}>
            <div style={{ fontSize: "2.5rem", marginBottom: "1rem" }}>⏳</div>
            <h3 style={{ fontWeight: 800 }}>Loading your orders...</h3>
          </div>
        ) : orders.length === 0 ? (
          <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "20px", padding: "4rem 2rem", textAlign: "center", boxShadow: "0 4px 20px rgba(15,23,42,0.02)" }}>
            <div style={{ fontSize: "3.5rem", marginBottom: "1rem" }}>📦</div>
            <h2 style={{ fontSize: "1.4rem", fontWeight: 900, color: "#0f172a", margin: "0 0 8px" }}>No Orders Yet</h2>
            <p style={{ color: "#64748b", maxWidth: "420px", margin: "0 auto 1.75rem", lineHeight: 1.6 }}>
              You haven't placed any wellness product orders yet. Discover hospital-grade orthopedic supports and diagnostics in our shop.
            </p>
            <button
              onClick={() => navigate("/shop")}
              style={{ padding: "0.85rem 2rem", background: "#2d6a4f", color: "#ffffff", border: "none", borderRadius: "9999px", fontWeight: 800, fontSize: "0.95rem", cursor: "pointer" }}
            >
              Start Shopping →
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
            {orders.map((order) => {
              const dateStr = new Date(order.createdAt || Date.now()).toLocaleDateString("en-IN", {
                year: "numeric",
                month: "short",
                day: "numeric",
              });
              const subINR = (order.totalAmount || 0) * 83;
              const totalINR = subINR >= 500 ? subINR : subINR + 49;

              return (
                <div key={order._id} style={{ background: "#ffffff", border: "1.5px solid #e2e8f0", borderRadius: "20px", overflow: "hidden", boxShadow: "0 6px 24px rgba(15,23,42,0.03)" }}>
                  {/* Order Top Banner */}
                  <div style={{ background: "#f8fafc", padding: "1.25rem 1.5rem", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1rem" }}>
                    <div style={{ display: "flex", gap: "2rem", flexWrap: "wrap" }}>
                      <div>
                        <span style={{ fontSize: "0.75rem", fontWeight: 800, color: "#64748b", textTransform: "uppercase", display: "block" }}>Order Placed</span>
                        <span style={{ fontSize: "0.9rem", fontWeight: 800, color: "#0f172a", display: "flex", alignItems: "center", gap: "6px" }}>
                          <FontAwesomeIcon icon={faCalendarAlt} style={{ color: "#2d6a4f" }} /> {dateStr}
                        </span>
                      </div>
                      <div>
                        <span style={{ fontSize: "0.75rem", fontWeight: 800, color: "#64748b", textTransform: "uppercase", display: "block" }}>Total Paid</span>
                        <span style={{ fontSize: "1rem", fontWeight: 900, color: "#2d6a4f" }}>₹{totalINR.toFixed(0)}</span>
                      </div>
                      <div>
                        <span style={{ fontSize: "0.75rem", fontWeight: 800, color: "#64748b", textTransform: "uppercase", display: "block" }}>Status</span>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "#dcfce7", color: "#166534", padding: "2px 10px", borderRadius: "9999px", fontSize: "0.8rem", fontWeight: 800 }}>
                          <FontAwesomeIcon icon={faCheckCircle} /> {order.orderStatus || "Processing"}
                        </span>
                      </div>
                    </div>

                    <div style={{ fontSize: "0.85rem", fontWeight: 800, color: "#475569", background: "#ffffff", padding: "0.45rem 1rem", borderRadius: "9999px", border: "1px solid #cbd5e1" }}>
                      Order #{String(order._id).slice(-8).toUpperCase()}
                    </div>
                  </div>

                  {/* Items list */}
                  <div style={{ padding: "1.5rem" }}>
                    <h4 style={{ margin: "0 0 1rem", fontSize: "0.9rem", fontWeight: 800, color: "#334155", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Ordered Items ({order.items?.length || 0}):
                    </h4>
                    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                      {(order.items || []).map((item, idx) => (
                        <div key={idx} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.85rem 1rem", background: "#f8fafc", borderRadius: "12px", border: "1px solid #f1f5f9" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <div style={{ width: "40px", height: "40px", borderRadius: "10px", background: "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.2rem", color: "#2d6a4f" }}>
                              <FontAwesomeIcon icon={faBoxOpen} />
                            </div>
                            <div>
                              <div style={{ fontWeight: 800, color: "#0f172a", fontSize: "0.95rem" }}>{item.title || "Wellness Product"}</div>
                              <div style={{ fontSize: "0.8rem", color: "#64748b", fontWeight: 600 }}>Quantity: {item.qty}</div>
                            </div>
                          </div>
                          <div style={{ fontWeight: 900, color: "#0f172a", fontSize: "1rem" }}>
                            ₹{(((item.price || 0) * 83) * item.qty).toFixed(0)}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Delivery Address */}
                    <div style={{ marginTop: "1.5rem", padding: "1rem", background: "#f0fdf4", border: "1px dashed #86efac", borderRadius: "14px", display: "flex", alignItems: "flex-start", gap: "10px", color: "#166534" }}>
                      <FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginTop: "3px", fontSize: "1.1rem" }} />
                      <div style={{ fontSize: "0.88rem" }}>
                        <strong style={{ display: "block", marginBottom: "2px" }}>Delivery Address:</strong>
                        {order.shippingAddress?.street}, {order.shippingAddress?.city}, {order.shippingAddress?.state} — <strong>{order.shippingAddress?.zip}</strong> ({order.shippingAddress?.country || "India"})
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PatientPortalLayout>
  );
};

export default PatientOrders;
